import { Page, PermissionLevel } from "../types/Authorization";
import { NavCategory } from "../layouts/Sidebar";

/**
 * Which pages a user may actually reach.
 *
 * Shared by the sidebar and the Ctrl+K command palette so the two can never
 * disagree: a palette that offered a destination the sidebar hides would be
 * confusing at best, and would advertise the existence of pages the user isn't
 * meant to know about.
 *
 * This governs *visibility only* — the route itself is still guarded by
 * PrivateRoute and the API by @Authorized. Nothing here is the security boundary.
 */

/**
 * Is a single "PageName:action" permission granted?
 * @param userPages - The user's pages, from UserPermissionsContext.
 * @param permissionString - e.g. "Missions:read".
 */
export const isPermissionGranted = (userPages: Page[], permissionString: string): boolean => {
    const parts = permissionString.split(":");
    if (parts.length !== 2) return false;

    const [pageName, action] = parts;
    const page = userPages.find((p: Page) => p.name.toLowerCase() === pageName.toLowerCase());
    if (!page) return false;

    const permissionValue = (page.permissions as any)[action];
    return !!permissionValue
        && permissionValue !== PermissionLevel.No
        && permissionValue !== PermissionLevel.Disabled
        && permissionValue !== PermissionLevel.NotSet;
};

/**
 * Filter the nav down to what this user can reach, dropping any category left
 * empty. Extracted verbatim from the sidebar's original inline logic.
 *
 * @param nav - The full nav manifest (see Sidebar's `menuItems`).
 * @param userPages - The user's pages, from UserPermissionsContext.
 */
export const filterNavByPermissions = (nav: NavCategory[], userPages: Page[]): NavCategory[] => {
    return nav
        .map((category) => ({
            // Keep the sidebar's presentation fields (key, kind, section, icon…).
            ...category,
            items: category.items.filter((item) => {
                // Multi-permission mode: visible if the user has ANY of the listed permissions.
                if (item.anyPermissions && item.anyPermissions.length > 0) {
                    return item.anyPermissions.some(permission => isPermissionGranted(userPages, permission));
                }

                const { name, permission } = item;
                const requestedPage = userPages.find(
                    (page: Page) => page.name.toLowerCase() === name.toLowerCase(),
                );
                if (requestedPage === undefined) return false;

                if (permission) {
                    const parts = permission.split(":");
                    if (parts.length === 2) {
                        const action = parts[1];
                        const permissionValue = (requestedPage.permissions as any)[action];
                        return permissionValue
                            && permissionValue !== PermissionLevel.No
                            && permissionValue !== PermissionLevel.Disabled
                            && permissionValue !== PermissionLevel.NotSet;
                    }
                }

                // Fallback: is the page generally enabled?
                return requestedPage.permissions.enabled !== false;
            }),
        }))
        .filter(category => category.items.length > 0);
};
